import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ClipboardList, ChevronRight, AlertTriangle, Pill, Activity, Clock,
  Calendar, RefreshCw, Loader2, Shield, FileText, User, Stethoscope,
  Sun, Sunrise
} from 'lucide-react';
import { toast } from 'sonner';
import { QualityChipStrip } from '../components/quality/QualityChipStrip';

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
}

export default function PreVisitDashboard() {
  const navigate = useNavigate();
  const [packets, setPackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateMode, setDateMode] = useState('today');
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [huddleMode, setHuddleMode] = useState(false);

  const getDateStr = useCallback(() => {
    const d = new Date();
    if (dateMode === 'tomorrow') d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  }, [dateMode]);

  const fetchPackets = useCallback(async () => {
    try {
      const dateStr = getDateStr();
      const params = new URLSearchParams({ date: dateStr });
      if (selectedProvider !== 'all') params.append('provider_id', selectedProvider);
      const res = await api.get(`/pre-visit/packets?${params}`);
      setPackets(res.data.packets || []);
      // Extract unique providers
      const provMap = {};
      (res.data.packets || []).forEach(p => {
        if (p.provider_id && p.provider_name) provMap[p.provider_id] = p.provider_name;
      });
      setProviders(Object.entries(provMap).map(([id, name]) => ({ id, name })));
    } catch {
      toast.error('Failed to load prep packets');
    } finally {
      setLoading(false);
    }
  }, [getDateStr, selectedProvider]);

  useEffect(() => { fetchPackets(); }, [fetchPackets]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/pre-visit/refresh', { date: getDateStr(), provider_id: selectedProvider !== 'all' ? selectedProvider : null });
      await fetchPackets();
      toast.success('Prep packets refreshed');
    } catch { toast.error('Refresh failed'); }
    finally { setRefreshing(false); }
  };

  const flagIcon = (type) => {
    if (type === 'warning') return <AlertTriangle className="w-3 h-3 text-amber-500" />;
    if (type === 'action') return <Clock className="w-3 h-3 text-blue-500" />;
    return <Activity className="w-3 h-3 text-slate-400" />;
  };

  const contextBadge = (ctx) => {
    if (ctx === 'acute') return <Badge variant="outline" className="text-[9px] h-4 bg-red-50 text-red-600 border-red-200">Acute</Badge>;
    if (ctx === 'chronic_followup') return <Badge variant="outline" className="text-[9px] h-4 bg-blue-50 text-blue-600 border-blue-200">Chronic F/U</Badge>;
    if (ctx === 'annual_wellness') return <Badge variant="outline" className="text-[9px] h-4 bg-emerald-50 text-emerald-600 border-emerald-200">Wellness</Badge>;
    return <Badge variant="outline" className="text-[9px] h-4">General</Badge>;
  };

  // Huddle mode: filter to complex/flagged patients
  const displayPackets = huddleMode
    ? packets.filter(p => p.flags.length >= 2 || p.med_count >= 8 || p.pending_tasks.length > 0 || p.medication_summary?.queued_refills > 0)
    : packets;

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="flex flex-col h-full" data-testid="previsit-dashboard">
      {/* Header */}
      <div className="px-6 py-3 border-b bg-white/80 backdrop-blur-xl flex items-center gap-3 shrink-0">
        <ClipboardList className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Pre-Visit Planning</h1>
        <div className="flex-1" />

        {/* Date toggle */}
        <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
          <button className={`px-3 py-1 text-xs rounded-md transition-colors ${dateMode === 'today' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`}
            onClick={() => setDateMode('today')} data-testid="previsit-today-btn">
            <Sun className="w-3 h-3 inline mr-1" /> Today
          </button>
          <button className={`px-3 py-1 text-xs rounded-md transition-colors ${dateMode === 'tomorrow' ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`}
            onClick={() => setDateMode('tomorrow')} data-testid="previsit-tomorrow-btn">
            <Sunrise className="w-3 h-3 inline mr-1" /> Tomorrow
          </button>
        </div>

        {/* Provider filter */}
        <Select value={selectedProvider} onValueChange={setSelectedProvider}>
          <SelectTrigger className="w-44 h-8 text-xs" data-testid="previsit-provider-filter">
            <SelectValue placeholder="All Providers" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Providers</SelectItem>
            {providers.map(p => (
              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Huddle mode toggle */}
        <button className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${huddleMode ? 'bg-primary text-white border-primary' : 'border-border text-muted-foreground hover:text-foreground'}`}
          onClick={() => setHuddleMode(!huddleMode)} data-testid="previsit-huddle-toggle">
          <Stethoscope className="w-3 h-3 inline mr-1" /> Huddle
        </button>

        {/* Refresh */}
        <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={refresh} disabled={refreshing} data-testid="previsit-refresh-btn">
          <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>

        <Badge variant="outline" className="text-xs">{displayPackets.length} patients</Badge>
      </div>

      {/* Patient List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2 max-w-5xl mx-auto" data-testid="previsit-patient-list">
          {displayPackets.length === 0 ? (
            <div className="text-center py-16">
              <ClipboardList className="w-10 h-10 mx-auto mb-3 text-muted-foreground/20" />
              <p className="text-sm text-muted-foreground">
                {huddleMode ? 'No flagged patients for huddle review' : 'No patients scheduled'}
              </p>
            </div>
          ) : (
            displayPackets.map((packet, idx) => (
              <Card key={packet.id || idx} className="shadow-none hover:shadow-sm transition-shadow cursor-pointer group"
                onClick={() => navigate(`/chart/${packet.patient_id}`)}
                data-testid={`previsit-patient-row-${idx}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Time + Type */}
                    <div className="w-16 shrink-0 text-center">
                      <p className="text-xs font-mono font-semibold">{formatTime12(packet.time)}</p>
                      {contextBadge(packet.visit_context)}
                    </div>

                    {/* Patient Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold">{packet.patient_name}</span>
                        <span className="text-[10px] text-muted-foreground">{packet.visit_type}</span>
                        <span className="text-[10px] text-muted-foreground">{packet.provider_name}</span>
                        <ChevronRight className="w-3 h-3 text-muted-foreground/30 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>

                      {/* Active Problems */}
                      {packet.active_problems.length > 0 && (
                        <div className="flex items-center gap-1 flex-wrap mb-1">
                          {packet.active_problems.slice(0, 4).map((p, i) => (
                            <Badge key={i} variant="outline" className={`text-[9px] h-4 ${p.classification === 'chronic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                              {p.name}{p.icd ? ` (${p.icd})` : ''}
                            </Badge>
                          ))}
                          {packet.active_problems.length > 4 && (
                            <span className="text-[9px] text-muted-foreground">+{packet.active_problems.length - 4} more</span>
                          )}
                        </div>
                      )}

                      {/* Flags */}
                      {packet.flags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          {packet.flags.map((f, i) => (
                            <span key={i} className="flex items-center gap-1 text-[10px]">
                              {flagIcon(f.type)} {f.text}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Recent Changes */}
                      {packet.recent_changes.length > 0 && (
                        <p className="text-[10px] text-muted-foreground line-clamp-1">
                          Last: {packet.recent_changes[0].summary}
                        </p>
                      )}

                      {/* Quality Chip Strip — compact, per-patient card */}
                      {packet.patient_id && (
                        <div className="mt-1.5" onClick={e => e.stopPropagation()}>
                          <QualityChipStrip
                            patientId={packet.patient_id}
                            context="pre_visit"
                            density="compact"
                            className="gap-1"
                          />
                        </div>
                      )}
                    </div>

                    {/* Right Summary */}
                    <div className="w-28 shrink-0 text-right space-y-0.5">
                      <div className="flex items-center justify-end gap-1 text-[10px]">
                        <Pill className="w-3 h-3 text-muted-foreground" />
                        <span>{packet.med_count} meds</span>
                      </div>
                      {packet.medication_summary?.high_risk_meds?.length > 0 && (
                        <Badge variant="outline" className="text-[8px] h-3.5 bg-red-50 text-red-600 border-red-200">
                          <Shield className="w-2.5 h-2.5 mr-0.5" /> High-risk
                        </Badge>
                      )}
                      {packet.care_gaps.length > 0 && (
                        <div className="text-[10px] text-amber-600">{packet.care_gaps.length} care gap{packet.care_gaps.length > 1 ? 's' : ''}</div>
                      )}
                      {packet.pending_tasks.length > 0 && (
                        <div className="text-[10px] text-blue-600">{packet.pending_tasks.length} task{packet.pending_tasks.length > 1 ? 's' : ''}</div>
                      )}
                      <div className="text-[9px] text-muted-foreground">{packet.allergies_summary}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
